import { IZone, IZoneServiceItem } from '../../../models';
import { Box, Button, Grid, Typography } from '@mui/material';
import React from 'react';
import { useZoneServiceLogFormManager } from './zone-service-log-form.hook';
import { capitalizeFirstLetter } from '../../../utils';
import { useAppSelector } from '../../../store/store.hook';
import { selectEmployeeCode } from '../../../store/slices';
import { CodeServiceButton as ServiceButton } from '../../../components/buttons';
import { AddAPhoto } from '@mui/icons-material';
import { useErrorToast } from '../../../hooks/toasts/error-toast.hook';

type DefaultServiceItem = 'serviced' | 'cleaned' | 'refilled' | 'sanitized' | 'disinfected';

export const ZoneServiceLogForm = React.forwardRef((props: { zone: IZone; next: () => void }, ref) => {
  const { zone, next } = props;
  const defaultServiceItems: DefaultServiceItem[] = ['cleaned', 'serviced', 'refilled', 'sanitized', 'disinfected'];
  const employeeCode = useAppSelector(selectEmployeeCode) as string;
  const [{ formManager }] = useZoneServiceLogFormManager({ zone, employeeCode, next });
  const errorToast = useErrorToast();

  React.useImperativeHandle(
    ref,
    () => ({
      onSubmit: () => {
        formManager.handleSubmit();
      },
    }),
    [],
  );

  const handleClickDefaultServiceItem = (item: DefaultServiceItem) => {
    formManager?.setFieldValue(item, !formManager.values[item]);
  };
  const defaultServiceItemIsSelected = (item: DefaultServiceItem) => {
    return formManager?.values[item] ?? false;
  };

  const handleClickServiceItem = (item: IZoneServiceItem) => {
    const items = formManager?.values.serviceItems ?? [];
    const itemIndex = items.findIndex((s) => s.id === item.id);
    if (itemIndex > -1) {
      items[itemIndex].serviced = !items[itemIndex].serviced;
      formManager?.setFieldValue('serviceItems', items);
    }
  };
  const serviceItemIsSelected = (item: IZoneServiceItem): boolean => {
    return formManager?.values.serviceItems.find((s) => s.id === item.id)?.serviced ?? false;
  };

  const handleUploadedFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (!files || files.length === 0) {
      return;
    }

    const existingFiles = Object.values(formManager?.values.files);
    const totalNumberOfFiles = existingFiles.length + files.length;
    if (totalNumberOfFiles > 3) {
      // alert the user
      errorToast.open({ message: 'file limit of 3 exceeded' });
      return;
    }

    formManager?.setFieldValue('files', existingFiles.concat(Object.values(files)));
  };

  return (
    <Box>
      <Grid container columns={3} spacing={2}>
        {defaultServiceItems.map((s) => (
          <Grid item xs={1} key={s}>
            <ServiceButton selected={defaultServiceItemIsSelected(s)} onClick={() => handleClickDefaultServiceItem(s)}>
              <Typography>{capitalizeFirstLetter(s)}</Typography>
            </ServiceButton>
          </Grid>
        ))}
        {zone.service_items.map((s, i) => (
          <Grid item xs={1} key={i}>
            <ServiceButton selected={serviceItemIsSelected(s)} onClick={() => handleClickServiceItem(s)}>
              <Typography>{capitalizeFirstLetter(s.name)}</Typography>
            </ServiceButton>
          </Grid>
        ))}
        <Grid item xs={1}>
          <Button
            component={'label'}
            color="primary"
            variant="outlined"
            sx={{
              minHeight: 100,
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <AddAPhoto />
              <br />
              <Typography variant="caption">{formManager?.values.files.length} / 3</Typography>
            </Box>
            <input type="file" accept="image/*" multiple hidden onChange={handleUploadedFiles} />
          </Button>
        </Grid>
      </Grid>
      {Object.values(formManager?.values.files).length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Grid container columns={3} spacing={2}>
            {Object.values(formManager?.values.files)?.map((file, i) => (
              <Grid item xs={1} key={i}>
                <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%' }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
});
